/** A `scan` step's parse rules (design §6.4) and the scan TESTER: paste (or
    scan) a sample payload and see the parsed fields and the sets they become —
    parsed on the server by the same operation the runtime calls, so the regex
    dialect and the GS1 reading are the real ones. */

import { useState } from 'react'
import { FlaskConical, Loader2 } from 'lucide-react'
import { parseApiError } from '@twasta/page-runtime/txn'
import { testWizardScan } from '@/api/wizards'
import type { WizardScanResponse } from '@twasta/page-runtime/wizard/contract'
import ExprInput from './ExprInput'
import { KeyValueEditor, TargetPicker } from './pickers'
import { useWizardDesign } from './context'
import type { TokenGroup } from './context'
import type { WizardScanParse, WizardStep } from './types'
import {
  AddBtn, Badge, Field, Grid2, RemoveBtn, SelectInput, StringList, Sub, TextInput,
  cardStyle, hintStyle, monoAreaStyle, smallBtn, tdStyle, thStyle,
} from './ui'

type RuleKind = 'pattern' | NonNullable<WizardScanParse['format']>

const RULES: Array<{ value: RuleKind; label: string }> = [
  { value: 'gs1', label: 'GS1 (01 GTIN, 10 batch, 17 expiry, 30 qty …)' },
  { value: 'pattern', label: 'Pattern — regex with named groups' },
  { value: 'json', label: 'JSON' },
  { value: 'kv', label: 'key=value pairs' },
  { value: 'delimited', label: 'Delimited fields' },
  { value: 'url', label: 'A URL — its query parameters' },
  { value: 'raw', label: 'The whole text → {scan.value}' },
]

export const SCAN_TOKENS: TokenGroup[] = [{
  label: 'Scan',
  tokens: ['{scan.value}', '{scan.rule}', '{scan.01}', '{scan.10}', '{scan.17}', '{scan.21}', '{scan.30|1}'],
}]

function ruleOf(p: WizardScanParse): RuleKind {
  return p.pattern !== undefined ? 'pattern' : (p.format || 'raw')
}

export function ParseRuleEditor({ parse, onChange, withMap, formNo }: {
  parse: WizardScanParse
  onChange: (p: WizardScanParse) => void
  /** Alternatives carry their own name + map. */
  withMap?: boolean
  formNo?: string
}) {
  const rule = ruleOf(parse)
  const keep = { name: parse.name, map: parse.map }
  return (
    <div>
      <Grid2>
        <Field label="Read it as">
          <SelectInput value={rule} options={RULES}
            onChange={(r) => onChange(r === 'pattern'
              ? { ...keep, pattern: '' }
              : { ...keep, format: r as WizardScanParse['format'] })} />
        </Field>
        {withMap && (
          <Field label="Rule name ({scan.rule})">
            <TextInput value={parse.name} onChange={(name) => onChange({ ...parse, name: name || undefined })} placeholder="pallet" />
          </Field>
        )}
      </Grid2>
      {rule === 'pattern' && (
        <Field label="Pattern (named groups become {scan.NAME})">
          <TextInput mono value={parse.pattern} onChange={(pattern) => onChange({ ...parse, pattern })}
            placeholder="^PO:(?<po_no>\w+)$" />
        </Field>
      )}
      {rule === 'kv' && (
        <Field label="Pair separators">
          <TextInput mono value={parse.separators} onChange={(separators) => onChange({ ...parse, separators: separators || undefined })}
            placeholder=";&|" />
        </Field>
      )}
      {rule === 'delimited' && (
        <Grid2>
          <Field label="Separator">
            <TextInput mono value={parse.sep} onChange={(sep) => onChange({ ...parse, sep })} placeholder="|" />
          </Field>
          <Field label="Field names, in order">
            <StringList values={parse.fields} onChange={(fields) => onChange({ ...parse, fields })} />
          </Field>
        </Grid2>
      )}
      {rule === 'url' && (
        <label style={{ fontSize: 13.5 }}>
          <input type="checkbox" checked={parse.params !== false}
            onChange={(e) => onChange({ ...parse, params: e.target.checked })} /> Read the query parameters
        </label>
      )}
      {withMap && (
        <Field label="Set (target → template) when this rule matched">
          <KeyValueEditor value={parse.map} onChange={(map) => onChange({ ...parse, map })} formNo={formNo}
            extraTokens={SCAN_TOKENS}
            keyRender={(k, setK) => <TargetPicker value={k} onChange={setK} formNo={formNo} />} />
        </Field>
      )}
    </div>
  )
}

function ScanTester({ step }: { step: WizardStep }) {
  const ctx = useWizardDesign()
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [res, setRes] = useState<WizardScanResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const run = async () => {
    setBusy(true)
    setError(null)
    setRes(null)
    try {
      setRes(await testWizardScan(ctx.project, ctx.model, step.id, text, { header: {}, line: {} }))
    } catch (err) {
      setError(parseApiError(err).message || 'The test failed')
    } finally {
      setBusy(false)
    }
  }
  const fields = Object.entries(res?.fields || {})
  return (
    <div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} spellCheck={false}
        placeholder={'(01)09501101530003(10)AB-123(17)270131(30)12\nor scan into this box — a hardware scanner types and sends Enter'}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void run() } }}
        style={{ ...monoAreaStyle, minHeight: 56 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
        <button type="button" style={smallBtn} onClick={run} disabled={busy || !text.trim()}>
          {busy ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <FlaskConical size={12} />}
          Test scan
        </button>
        {res && (res.ok
          ? <Badge tone="ok">matched{res.rule ? `: ${res.rule}` : ''}</Badge>
          : <Badge tone="error">no rule matched</Badge>)}
        {res && !res.expect_ok && <Badge tone="error">expect failed</Badge>}
      </div>
      {error && <div style={{ ...hintStyle, color: 'var(--color-error, #d33)' }}>{error}</div>}
      {res?.message && <div style={hintStyle}>{res.message}</div>}
      {res && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 8 }}>
          <div>
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Parsed fields</div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={thStyle}>Field</th><th style={thStyle}>Value</th></tr></thead>
              <tbody>
                {fields.length === 0 && <tr><td style={tdStyle} colSpan={2}>—</td></tr>}
                {fields.map(([k, v]) => (
                  <tr key={k}><td style={tdStyle}><code>{`{scan.${k}}`}</code></td><td style={tdStyle}>{String(v ?? '')}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Sets</div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={thStyle}>Target</th><th style={thStyle}>Value</th></tr></thead>
              <tbody>
                {(res.sets || []).length === 0 && <tr><td style={tdStyle} colSpan={2}>—</td></tr>}
                {(res.sets || []).map((s, i) => (
                  <tr key={i}><td style={tdStyle}><code>{s.target}</code></td><td style={tdStyle}>{String(s.value ?? '')}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div style={hintStyle}>The test reads the model as it is on screen (saved or not), with an empty record.</div>
    </div>
  )
}

export default function ScanEditor({ step, onChange }: {
  step: WizardStep
  onChange: (p: Partial<WizardStep>) => void
}) {
  const ctx = useWizardDesign()
  const alts = step.alternatives || []
  return (
    <div>
      <Field label="Prompt">
        <ExprInput mode="template" value={step.prompt} onChange={(prompt) => onChange({ prompt })} formNo={step.form_no} />
      </Field>
      <Sub title="How to read it">
        <ParseRuleEditor parse={step.parse || { format: 'raw' }} onChange={(parse) => onChange({ parse })} formNo={step.form_no} />
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 4 }}>
            Alternatives — tried in order when the payload can be one of several labels
          </div>
          {alts.map((a, i) => (
            <div key={i} style={{ ...cardStyle, padding: 8, marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                {!ctx.readOnly && <RemoveBtn onClick={() => onChange({ alternatives: alts.filter((_, j) => j !== i).length ? alts.filter((_, j) => j !== i) : undefined })} />}
              </div>
              <ParseRuleEditor parse={a} withMap formNo={step.form_no}
                onChange={(na) => onChange({ alternatives: alts.map((x, j) => (j === i ? na : x)) })} />
            </div>
          ))}
          {!ctx.readOnly && <AddBtn onClick={() => onChange({ alternatives: [...alts, { name: `rule_${alts.length + 1}`, format: 'raw', map: {} }] })}>Add alternative</AddBtn>}
        </div>
      </Sub>
      <Sub title="Set from the scan">
        <KeyValueEditor value={step.map} onChange={(map) => onChange({ map })} formNo={step.form_no}
          extraTokens={SCAN_TOKENS} valuePlaceholder="{scan.10}"
          keyRender={(k, setK) => <TargetPicker value={k} onChange={setK} formNo={step.form_no} />} />
        <div style={hintStyle}>Each set fires the column’s item change, in order. <code>{'{scan.30|1}'}</code> = the scanned quantity, else 1.</div>
      </Sub>
      <Sub title="Check and loop">
        <Field label="Expect (a wrong scan is rejected and changes nothing)">
          <ExprInput value={step.expect} onChange={(expect) => onChange({ expect: expect || undefined })}
            formNo={step.form_no} extraTokens={SCAN_TOKENS} placeholder="{scan.01} == {line.gtin}" />
        </Field>
        <Grid2>
          <Field label="Keep scanning until (button:<label> or an expression)">
            <ExprInput mode="template" value={step.loop?.until}
              onChange={(until) => onChange({ loop: until || step.loop?.or_when ? { ...step.loop, until: until || undefined } : undefined })}
              formNo={step.form_no} placeholder="button:Done" />
          </Field>
          <Field label="…or when">
            <ExprInput value={step.loop?.or_when}
              onChange={(or_when) => onChange({ loop: or_when || step.loop?.until ? { ...step.loop, or_when: or_when || undefined } : undefined })}
              formNo={step.form_no} placeholder="countif({lines.picked_qty} < {lines.pick_qty}) == 0" />
          </Field>
          <Field label="Good scan">
            <SelectInput value={step.feedback?.ok || 'beep'} options={['beep', 'none']}
              onChange={(ok) => onChange({ feedback: { ...step.feedback, ok: ok as 'beep' | 'none' } })} />
          </Field>
          <Field label="Rejected scan">
            <SelectInput value={step.feedback?.error || 'buzz'} options={['buzz', 'none']}
              onChange={(error) => onChange({ feedback: { ...step.feedback, error: error as 'buzz' | 'none' } })} />
          </Field>
        </Grid2>
        <label style={{ fontSize: 13.5 }}>
          <input type="checkbox" checked={step.manual_entry !== false}
            onChange={(e) => onChange({ manual_entry: e.target.checked })} /> Offer a field to type the code
        </label>
      </Sub>
      <Sub title="Scan tester">
        <ScanTester step={step} />
      </Sub>
    </div>
  )
}
